import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { GoldDots, PageHead } from "@/components/Brand";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { errMsg, formatDate, startPlanPath, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";

const Status = ({ status }) => (
  <span className={`text-xs font-medium rounded-full px-3 py-1 ${status === "completed" ? "bg-sage-soft text-sage-deep" : "bg-alabaster-alt text-charcoal"}`}>
    {status === "completed" ? "Completed" : "Active"}
  </span>
);

const PlanRow = ({ pl, onComplete, onDelete }) => (
  <li className="ahu-card p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid={`plan-row-${pl.id}`}>
    <Link to={`/plans/${pl.id}`} className="min-w-0" data-testid={`plan-open-${pl.id}`}>
      <p className="font-display text-lg text-charcoal truncate">{pl.title}</p>
      <p className="text-sm text-ink-soft">{formatDate(pl.created_at)} · {pl.payment_hold
        ? <span className="text-charcoal font-medium" data-testid={`plan-hold-${pl.id}`}>Update your card to open</span>
        : `${(pl.actions_done || []).filter(Boolean).length} of 3 actions done`}</p>
    </Link>
    <div className="flex items-center gap-4 shrink-0">
      <Status status={pl.status} />
      {pl.status === "active" && !pl.payment_hold && <button className="btn-quiet text-sm" onClick={() => onComplete(pl.id)} data-testid={`plan-complete-${pl.id}`}>Complete</button>}
      <ConfirmDialog title="Delete this Action Plan?" body="This removes the plan from your account. It can't be undone." confirmLabel="Delete Action Plan"
        onConfirm={() => onDelete(pl.id)} testId={`plan-delete-${pl.id}`}
        trigger={<button className="text-sm text-warn underline underline-offset-4" data-testid={`plan-delete-${pl.id}-btn`}>Delete</button>} />
    </div>
  </li>
);

export default function PlansPage() {
  const api = useApi();
  const nav = useNavigate();
  const { me, refresh } = useMe();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const load = () => api.get("/plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [api]);

  const complete = async (id) => {
    try { await api.post(`/plans/${id}/status`, { status: "completed" }); toast.success("Plan marked completed"); load(); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/plans/${id}`); toast.success("Action Plan deleted"); load(); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const groups = (plans || []).reduce((g, p) => ({ ...g, [p.area]: [...(g[p.area] || []), p] }), {});
  return (
    <AppShell>
      <PageHead title="Your plans" />
      {params.get("limit") && (
        <div className="ahu-card p-5 mb-8 border-gold/50" data-testid="active-limit-note">You have 3 active plans. Complete one to start the next.</div>
      )}
      {!plans ? <div className="py-16 flex justify-center"><GoldDots /></div> : plans.length === 0 ? (
        <div className="ahu-card p-8 text-center" data-testid="plans-empty">
          <p className="text-ink-soft">You don't have a plan yet.</p>
          <button className="btn-gold mt-6" onClick={() => nav(startPlanPath(me))} data-testid="plans-start-btn">Start a new plan</button>
        </div>
      ) : (
        <div className="space-y-10" data-testid="plans-list">
          {Object.entries(groups).map(([area, list]) => (
            <section key={area}>
              <h2 className="text-lg sm:text-xl mb-4">{area}</h2>
              <ul className="space-y-3">{list.map((pl) => <PlanRow key={pl.id} pl={pl} onComplete={complete} onDelete={remove} />)}</ul>
            </section>
          ))}
        </div>
      )}
    </AppShell>
  );
}
